import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ChevronLeft, MessageCircle, Search, Plus } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { translate } from "@/lib/i18n";
import { track, siteUrl } from "@/lib/api";
import { CategoryIcon } from "@/components/dm/ui";

const PRIORITY = ["food-groceries", "beauty-hair", "restaurants-catering", "shipping-barrels", "services", "fashion-tailoring"];

const Bilingual = ({ k, className = "", params }) => (
  <div className={className}>
    <div>{translate("en", k, params)}</div>
    <div className="opacity-80 italic">{translate("fr", k, params)}</div>
  </div>
);

const Step = ({ icon: I, en, fr }) => (
  <div className="flex items-center gap-3">
    <span className="w-12 h-12 rounded-full bg-[#F59E0B] text-[#1E4D3B] flex items-center justify-center shrink-0"><I className="w-6 h-6" /></span>
    <div className="leading-tight"><div className="font-extrabold text-lg">{en}</div><div className="text-white/80 italic">{fr}</div></div>
  </div>
);

export default function Flyer() {
  const { t, meta } = useApp();
  const navigate = useNavigate();
  const url = siteUrl();
  const cats = PRIORITY.map((id) => meta.categories.find((c) => c.id === id)).filter(Boolean);
  const hubs = meta.hubs.filter((h) => h.launch_status === "active");
  useEffect(() => { track("share_click", { meta: { target: "app", channel: "flyer_view" } }); }, []);
  return (
    <div className="min-h-screen bg-[#E8E2D9] print:bg-white py-6 print:py-0" data-testid="flyer-page">
      <style>{"@page { size: letter portrait; margin: 0; } @media print { html, body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }"}</style>
      <div className="max-w-[8.5in] mx-auto px-4 print:px-0 mb-4 flex flex-wrap gap-3 items-center print:hidden">
        <button data-testid="flyer-back" onClick={() => navigate(-1)} className="press min-h-[48px] px-5 rounded-full bg-[#FFFDF9] font-bold inline-flex items-center gap-1"><ChevronLeft className="w-5 h-5" />{t("flyer_back")}</button>
        <button data-testid="flyer-print" onClick={() => { track("share_click", { meta: { target: "app", channel: "flyer_print" } }); window.print(); }} className="press min-h-[48px] px-6 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold inline-flex items-center gap-2"><Printer className="w-5 h-5" />{t("flyer_print")}</button>
        <p className="text-sm text-[#6B5E55] basis-full">{t("flyer_tip")}</p>
      </div>
      <article data-testid="flyer-sheet" className="mx-auto w-full max-w-[8.5in] sm:aspect-[8.5/11] sm:overflow-hidden print:w-[8.5in] print:h-[11in] print:max-w-none print:aspect-auto bg-[#1E4D3B] text-white flex flex-col shadow-2xl print:overflow-hidden print:shadow-none">
        <div className="h-4 ankara-strip shrink-0" />
        <div className="flex-1 flex flex-col px-[6%] py-8 sm:py-[5%] gap-6 sm:gap-[3.5%]">
          <header className="text-center space-y-2">
            <div className="text-[#F59E0B] font-extrabold tracking-tight text-[clamp(2rem,7vw,4rem)] leading-none">Diaspomark</div>
            <Bilingual k="hero_title" className="text-[clamp(1.1rem,3.4vw,1.9rem)] font-extrabold leading-tight" />
          </header>
          <Bilingual k="hero_free" className="text-center text-[#FDE68A] font-bold text-[clamp(0.95rem,2.4vw,1.35rem)] leading-snug" />
          <div className="flex flex-col sm:flex-row print:flex-row items-center gap-[5%] justify-center">
            <div className="bg-[#FFFDF9] p-4 rounded-[28px] shadow-xl shrink-0" data-testid="flyer-qr">
              <QRCodeSVG value={url} size={260} level="H" fgColor="#1E4D3B" bgColor="#FFFDF9" marginSize={1} className="w-[min(52vw,260px)] h-auto print:w-[3.3in]"
                imageSettings={{ src: "/icon-192.png", height: 54, width: 54, excavate: true }} />
            </div>
            <div className="space-y-4">
              <Step icon={Search} en="Scan & browse — free" fr="Scannez et explorez — gratuit" />
              <Step icon={Plus} en="Sell, or ask for what you need" fr="Vendez ou demandez ce qu'il vous faut" />
              <Step icon={MessageCircle} en="Close the deal on WhatsApp" fr="Concluez sur WhatsApp" />
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {cats.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-sm font-semibold">
                <CategoryIcon icon={c.icon} className="w-4 h-4 text-[#F59E0B]" />{c.name_en} · <span className="italic opacity-80">{c.name_fr}</span>
              </span>
            ))}
          </div>
          <footer className="mt-auto text-center space-y-1.5">
            <div className="text-sm font-bold text-[#FDE68A]">{hubs.map((h) => h.name_fr === h.name_en ? h.name_en : `${h.name_en} / ${h.name_fr}`).join("  ·  ")}</div>
            <div data-testid="flyer-url" className="font-mono font-extrabold text-[clamp(1rem,2.6vw,1.5rem)] text-[#F59E0B] break-all">{url.replace(/^https?:\/\//, "")}</div>
            <Bilingual k="hero_trust" className="text-xs text-white/80" />
          </footer>
        </div>
        <div className="h-4 ankara-strip shrink-0" />
      </article>
    </div>
  );
}
